import React, { useEffect, useMemo, useRef, useState } from "react";
import { Page } from "@/components/PageConsole";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, CalendarPlus, AlertTriangle, MapPin, Clock } from "@/components/icons";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { useBalk } from "@/lib/balkState";
import { ErrorState } from "@/components/ListState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Code } from "@/components/Code";
import { PriorityBadge, FaultStatusBadge, formatNlDate } from "@/components/Badges";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// The screen you open while someone is telling you something. Capture first,
// classify later: one field, Enter, done. Everything below it is a rearrangement
// of data that already exists (punten, taken, storingen).

const isoWeekKey = (d) => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
};
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function MijnWerk() {
  const navigate = useNavigate();
  const { user, canWrite, canView } = useAuth();
  const { refresh: refreshBalk } = useBalk();
  const writable = canWrite();
  const inputRef = useRef(null);

  const [punten, setPunten] = useState([]);
  const [locations, setLocations] = useState([]);
  const [agenda, setAgenda] = useState([]);
  const [faults, setFaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [title, setTitle] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  // Lines whose POST failed. They are never thrown away: they render as retry
  // rows above the list, so a bad connection cannot silently eat a punt.
  const [failed, setFailed] = useState([]);
  const [retrying, setRetrying] = useState({});
  // Mirrors the input synchronously, because a setState updater is not
  // guaranteed to run before the next statement.
  const titleRef = useRef("");
  const writeTitle = (v) => { titleRef.current = v; setTitle(v); };

  const [scheduleFor, setScheduleFor] = useState(null);
  const [dueDate, setDueDate] = useState("");
  const [dueTime, setDueTime] = useState("");
  const [faultFor, setFaultFor] = useState(null);
  const [priority, setPriority] = useState("P3");
  const [faultLoc, setFaultLoc] = useState("__none__");
  const [locFor, setLocFor] = useState(null);
  const [detailFor, setDetailFor] = useState(null);
  const [newLoc, setNewLoc] = useState("__none__");
  const [justConverted, setJustConverted] = useState({});
  const [busy, setBusy] = useState(false);
  // Optimistic tick: the checkbox fills green the moment you tap it, before the
  // row leaves the list.
  const [ticked, setTicked] = useState({});
  // Correcting a punt you typed one-handed while someone was talking.
  const [editTitle, setEditTitle] = useState(null);
  const [savingTitle, setSavingTitle] = useState(false);

  const today = iso(new Date());
  const weekEnd = iso(new Date(Date.now() + 7 * 864e5));

  const load = async () => {
    setLoadError("");
    try {
      // Only ask for what this user may see: /tasks and /faults sit behind their own
      // module guards, and a 403 from the interceptor would bounce him off the page.
      const [p, l, a, f] = await Promise.all([
        api.get("/punten"),
        api.get("/locations"),
        canView("kalender") ? api.get(`/tasks/calendar?start=${today}&end=${weekEnd}`) : Promise.resolve({ data: [] }),
        canView("storingen") && user?.id ? api.get(`/faults?assigned_to=${user.id}&open_only=true`) : Promise.resolve({ data: [] }),
      ]);
      setPunten(p.data); setLocations(l.data); setAgenda(a.data); setFaults(f.data);
    } catch (e) {
      setLoadError(getErrorMessage(e, "Kan je werk niet laden."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const locMap = useMemo(() => Object.fromEntries(locations.map((l) => [l.location_id, l.name])), [locations]);
  // Converted punten stay in place for the rest of the visit, so you can see
  // which storing came out of what you just wrote down.
  const open = punten.filter((p) => p.status === "open" || justConverted[p.id]);
  const doneThisWeek = punten.filter((p) => p.done_at && isoWeekKey(new Date(p.done_at)) === isoWeekKey(new Date())).length;

  // "Zonder locatie" first: those are the ones captured on the phone that still need triage.
  const groups = useMemo(() => {
    const map = {};
    open.forEach((p) => { const k = p.location_id || "__none__"; (map[k] = map[k] || []).push(p); });
    return Object.entries(map)
      .map(([key, items]) => ({ key, name: key === "__none__" ? "Zonder locatie" : (locMap[key] || key), items }))
      .sort((a, b) => (a.key === "__none__" ? -1 : b.key === "__none__" ? 1 : a.name.localeCompare(b.name)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [punten, locMap, justConverted]);

  const todayItems = agenda.filter((i) => i.date === today && i.status !== "gedaan");
  const weekItems = agenda.filter((i) => i.date > today && i.status !== "gedaan");

  const capture = async () => {
    const t = title.trim();
    if (!t) return;
    capturedOnce.current = true;
    // Clear immediately: the next thing he hears must be typeable at once, and a
    // background refresh must never swallow an Enter or erase what came after.
    writeTitle("");
    setRefreshing(true);
    try {
      await api.post("/punten", { title: t, location_id: null });
      toast.success("Punt genoteerd");
    } catch (e) {
      // The line is kept no matter what he typed in the meantime.
      setFailed((prev) => [...prev, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, text: t }]);
      toast.error(`Niet opgeslagen: "${t}"`);
      setRefreshing(false);
      // Only put it back in the field when the field is empty, and only then
      // pre-select it — otherwise the next keystroke would wipe the newer line.
      if (!titleRef.current) {
        writeTitle(t);
        requestAnimationFrame(() => inputRef.current?.select());
      }
      return;
    }
    load().finally(() => setRefreshing(false));
    inputRef.current?.focus();
  };

  const retryFailed = async (f) => {
    setRetrying((prev) => ({ ...prev, [f.id]: true }));
    try {
      await api.post("/punten", { title: f.text, location_id: null });
      setFailed((prev) => prev.filter((x) => x.id !== f.id));
      toast.success("Punt genoteerd");
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, "Nog steeds niet opgeslagen."));
    } finally {
      setRetrying((prev) => { const next = { ...prev }; delete next[f.id]; return next; });
    }
  };

  // Capture speed is the whole point. On load we only grab focus with a mouse — on
  // a phone that would throw the keyboard over the list before Joey has read it.
  const capturedOnce = useRef(false);
  useEffect(() => {
    if (writable && window.matchMedia?.("(pointer: fine)").matches) inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ticking a punt off is undoable: the uncomplete endpoint is one tap away.
  const tick = async (p) => {
    setTicked((prev) => ({ ...prev, [p.id]: true }));
    try {
      await api.post(`/punten/${p.id}/complete`, {});
      toast.success("Afgehandeld", {
        action: {
          label: "Ongedaan maken",
          onClick: async () => {
            try {
              await api.post(`/punten/${p.id}/uncomplete`, {});
              toast.success("Teruggezet");
              setTicked((prev) => { const n = { ...prev }; delete n[p.id]; return n; });
              load();
            } catch (e) {
              toast.error(getErrorMessage(e, "Terugzetten mislukt."));
            }
          },
        },
      });
      load();
    } catch (e) {
      setTicked((prev) => { const n = { ...prev }; delete n[p.id]; return n; });
      toast.error(getErrorMessage(e, "Mislukt."));
    }
  };

  // Only the text changes. No date, no type: a punt that gets a date stops being
  // a punt, and correcting a typo must never do that behind his back.
  const saveTitle = async () => {
    const next = (editTitle || "").trim();
    if (!next || !detailFor || next === detailFor.title) { setEditTitle(null); return; }
    const id = detailFor.id;
    const before = detailFor.title;
    setSavingTitle(true);
    setPunten((prev) => prev.map((x) => (x.id === id ? { ...x, title: next } : x)));
    setDetailFor((d) => (d ? { ...d, title: next } : d));
    setEditTitle(null);
    try {
      await api.put(`/tasks/${id}`, { title: next });
      toast.success("Punt bijgewerkt");
      load();
    } catch (e) {
      setPunten((prev) => prev.map((x) => (x.id === id ? { ...x, title: before } : x)));
      setDetailFor((d) => (d && d.id === id ? { ...d, title: before } : d));
      toast.error(getErrorMessage(e, "Wijziging niet opgeslagen."));
    } finally {
      setSavingTitle(false);
    }
  };
  const doSchedule = async () => {
    if (!dueDate) { toast.error("Kies een datum."); return; }
    setBusy(true);
    try {
      await api.post(`/punten/${scheduleFor.id}/schedule`, { due_date: dueDate, due_time: dueTime || null });
      toast.success("In de agenda gezet");
      setScheduleFor(null); setDueDate(""); setDueTime(""); await load();
    } catch (e) { toast.error(getErrorMessage(e, "Mislukt.")); } finally { setBusy(false); }
  };
  const doToFault = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/punten/${faultFor.id}/to-fault`, {
        priority, location_id: faultLoc === "__none__" ? null : faultLoc,
      });
      toast.success(`Storing aangemaakt: ${data.fault_number}`);
      refreshBalk();
      setJustConverted((prev) => ({ ...prev, [faultFor.id]: data.fault_number }));
      setFaultFor(null); setPriority("P3"); await load();
    } catch (e) { toast.error(getErrorMessage(e, "Mislukt.")); } finally { setBusy(false); }
  };
  const doSetLocation = async () => {
    setBusy(true);
    try {
      await api.put(`/tasks/${locFor.id}`, { location_id: newLoc === "__none__" ? null : newLoc });
      toast.success("Locatie toegevoegd");
      setLocFor(null); await load();
    } catch (e) { toast.error(getErrorMessage(e, "Mislukt.")); } finally { setBusy(false); }
  };

  const PuntRow = ({ p }) => (
    <li className="flex flex-wrap items-center gap-2 py-1.5" data-testid={`punt-row-${p.id}`}>
      {writable && !p.converted_fault_id && (
        <button
          type="button"
          data-testid={`punt-toggle-${p.id}`}
          onClick={() => tick(p)}
          aria-label="Afvinken"
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded border-2 transition-colors sm:h-10 sm:w-10 ${
            ticked[p.id]
              ? "border-goed-rand bg-goed-vlak text-goed-tekst"
              : "border-lijn-sterk text-lijn-sterk hover:border-goed-rand hover:bg-goed-vlak hover:text-goed-tekst"
          }`}
        >
          <Check size={20} weight={ticked[p.id] ? "bold" : "regular"} />
        </button>
      )}
      <span className="min-w-0 flex-1">
        {/* On a phone the row IS the action: it opens the punt, and the three
            things you can do with it live in that dialog. Nine punten times three
            buttons made the fastest screen in the app the longest scroll. */}
        <button
          type="button"
          data-testid={`punt-open-${p.id}`}
          onClick={() => { setDetailFor(p); setEditTitle(null); }}
          className="flex min-h-12 w-full items-center text-left text-body text-foreground hover:underline sm:min-h-0"
        >
          <span className="line-clamp-2">{p.title}</span>
        </button>
        {p.created_by_name && (
          <span className="mt-0.5 block text-data text-muted-foreground" data-testid={`punt-author-${p.id}`}>
            {p.created_by_name}
          </span>
        )}
      </span>
      {p.converted_fault_id ? (
        <span className="shrink-0 rounded-sm border border-letop-rand bg-letop-vlak px-2 py-0.5 text-label font-semibold uppercase tracking-[0.07em] text-letop-tekst" data-testid={`punt-converted-${p.id}`}>
          Storing aangemaakt{justConverted[p.id] ? `: ${justConverted[p.id]}` : ""}
        </span>
      ) : writable && (
        <div className="hidden sm:flex sm:w-auto sm:gap-2">
          {/* Also when a location is already set: picking the wrong site is as easy
              as a typo, and it must be correctable. */}
          <Button variant="outline" className="h-9 px-3" data-testid={`punt-set-location-${p.id}`} onClick={() => { setLocFor(p); setNewLoc(p.location_id || "__none__"); }}>
            <MapPin size={18} className="mr-1.5" /> {p.location_id ? "Andere locatie" : "Locatie"}
          </Button>
          <Button variant="outline" className="h-9 px-3" data-testid={`punt-schedule-${p.id}`} onClick={() => { setScheduleFor(p); setDueDate(""); setDueTime(""); }}>
            <CalendarPlus size={18} className="mr-1.5" /> Plan in
          </Button>
          <Button variant="outline" className="h-9 px-3 text-letop-tekst" data-testid={`punt-to-fault-${p.id}`} onClick={() => { setFaultFor(p); setPriority("P3"); setFaultLoc(p.location_id || "__none__"); }}>
            <AlertTriangle size={18} className="mr-1.5" /> Is een storing
          </Button>
        </div>
      )}
    </li>
  );

  const nothingOpen = !loading && failed.length === 0 && groups.length === 0 && todayItems.length === 0 && weekItems.length === 0 && faults.length === 0;

  const FailedCard = () => (
    <Card className="border-letop-rand bg-letop-vlak p-4 sm:p-5" data-testid="mijnwerk-failed">
      <div className="mb-2 flex items-center gap-2 text-body font-semibold text-letop-tekst">
        <AlertTriangle size={18} /> Niet opgeslagen ({failed.length})
      </div>
      <ul className="divide-y divide-letop-rand">
        {failed.map((f) => (
          <li key={f.id} className="flex flex-wrap items-center gap-2 py-2" data-testid={`mijnwerk-failed-row-${f.id}`}>
            <span className="min-w-0 flex-1 text-body text-foreground">{f.text}</span>
            <Button
              variant="outline"
              className="h-11 sm:h-9"
              data-testid={`mijnwerk-retry-${f.id}`}
              onClick={() => retryFailed(f)}
              disabled={!!retrying[f.id]}
            >
              Opnieuw proberen
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );

  const consoleFacts = [
    `${groups.reduce((n, g) => n + g.items.length, 0)} open punten`,
    ...(todayItems.length ? [`${todayItems.length} vandaag`] : []),
    ...(faults.length ? [`${faults.length} storingen op mij`] : []),
  ].slice(0, 3);

  return (
    <Page console={{ variant: "view", title: "Mijn werk", facts: consoleFacts }}>
    <div className="space-y-4" data-testid="mijn-werk-page">
      {/* Capture first. Nothing competes with this field. Navy, not plate: this is
          a view screen, and the plate colour means "an object with a plate on it". */}
      {writable && (
      <div className="op-donker rounded bg-console px-4 py-4 sm:px-6" data-testid="mijnwerk-capture-block">
        <Input
          ref={inputRef}
          data-testid="mijnwerk-capture-input"
          value={title}
          onChange={(e) => writeTitle(e.target.value)}
          aria-busy={refreshing}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); capture(); } }}
          placeholder="Wat hoorde je net?"
          className="h-14 border-0 bg-white text-body text-foreground placeholder:text-muted-foreground"
        />
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-label font-semibold uppercase tracking-[0.07em] text-mist">
          <span>Enter bewaart het als punt</span>
          <span data-testid="mijnwerk-done-week">Deze week afgehandeld (heel de TD): {doneThisWeek}</span>
        </div>
      </div>
      )}

      {failed.length > 0 && <FailedCard />}

      {loadError ? (
        <ErrorState testid="mijnwerk-load-error" message={loadError} onRetry={load} />
      ) : loading ? (
        <p className="text-body text-muted-foreground">Laden...</p>
      ) : nothingOpen ? (
        <Card className="p-5" data-testid="mijnwerk-empty">
          <p className="text-body text-foreground">Niets open.</p>
          <p className="mt-1 text-body text-muted-foreground">
            {writable
              ? "Op dit scherm staat alles wat nog van jou is: losse punten, taken van vandaag en deze week, en de storingen die op jouw naam staan. Wat je hierboven intypt komt hier te staan."
              : "Op dit scherm staat wat er nog open is bij de TD: losse punten, taken en storingen."}
          </p>
        </Card>
      ) : (
        <>
          {groups.length > 0 && (
            <h2 className="text-label font-semibold uppercase tracking-[0.07em] text-muted-foreground" data-testid="mijnwerk-punten-heading">
              Openstaand (heel de TD)
            </h2>
          )}
          {groups.map((g) => (
            <Card key={g.key} className="p-4 sm:p-5" data-testid={`punten-group-${g.key}`}>
              <div className="mb-2 flex items-center gap-2 text-body font-semibold text-foreground">
                <MapPin size={18} className="text-muted-foreground" />
                {g.name} <span className="font-normal text-muted-foreground">({g.items.length})</span>
              </div>
              <ul className="divide-y divide-border">
                {g.items.map((p) => <PuntRow key={p.id} p={p} />)}
              </ul>
            </Card>
          ))}

          {(todayItems.length > 0 || weekItems.length > 0) && (
            <Card className="p-4 sm:p-5" data-testid="mijnwerk-agenda">
              <div className="mb-2 flex items-center gap-2 text-body font-semibold text-foreground">
                <Clock size={18} className="text-muted-foreground" /> Vandaag en deze week
              </div>
              <ul className="divide-y divide-border">
                {[...todayItems, ...weekItems].map((i) => (
                  <li
                    key={`${i.task_id}-${i.date}`}
                    data-testid={`mijnwerk-agenda-item-${i.task_id}`}
                    className="flex cursor-pointer items-center gap-3 py-3 hover:bg-accent"
                    onClick={() => navigate("/kalender")}
                  >
                    <Code size="small">{i.date === today ? "vandaag" : formatNlDate(i.date)}</Code>
                    {i.due_time && <Code size="small">{i.due_time}</Code>}
                    <span className="min-w-0 flex-1 truncate text-body text-foreground">{i.title}</span>
                    {i.location_id && <span className="hidden text-data text-muted-foreground sm:inline">{locMap[i.location_id]}</span>}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {faults.length > 0 && (
            <Card className="p-4 sm:p-5" data-testid="mijnwerk-faults">
              <div className="mb-2 flex items-center gap-2 text-body font-semibold text-foreground">
                <AlertTriangle size={18} className="text-muted-foreground" /> Aan mij toegewezen
              </div>
              <ul className="divide-y divide-border">
                {faults.map((f) => (
                  <li
                    key={f.id}
                    data-testid={`mijnwerk-fault-${f.id}`}
                    className="flex cursor-pointer flex-col gap-1 py-3 hover:bg-accent sm:flex-row sm:items-center sm:gap-3"
                    onClick={() => navigate(`/storingen/${f.id}`)}
                  >
                    <div className="flex items-center gap-2">
                      <PriorityBadge priority={f.priority} />
                      <Code>{f.fault_number}</Code>
                    </div>
                    <span className="min-w-0 flex-1 text-body text-foreground sm:truncate">{f.description || "(geen omschrijving)"}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-data text-muted-foreground">{f.location_name}</span>
                      <FaultStatusBadge status={f.status} />
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}

      {/* Plan in */}
      <Dialog open={!!scheduleFor} onOpenChange={(o) => !o && setScheduleFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Plan in</DialogTitle></DialogHeader>
          <DialogDescription>Met een datum wordt dit een agenda-afspraak.</DialogDescription>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Datum</Label><Input type="date" data-testid="punt-schedule-date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Tijd</Label><Input type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setScheduleFor(null)}>Annuleren</Button>
            <Button data-testid="punt-schedule-confirm" onClick={doSchedule} disabled={busy}>In agenda zetten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Is een storing */}
      <Dialog open={!!faultFor} onOpenChange={(o) => !o && setFaultFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Omzetten naar storing</DialogTitle></DialogHeader>
          <DialogDescription>Dit maakt een echte storing aan (met storingsnummer) en sluit het punt.</DialogDescription>
          <div className="space-y-1.5">
            <Label>Prioriteit</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="punt-fault-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="P1">P1 — kritiek (baan stil)</SelectItem>
                <SelectItem value="P2">P2 — hoog</SelectItem>
                <SelectItem value="P3">P3 — normaal</SelectItem>
                <SelectItem value="P4">P4 — laag</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Locatie</Label>
            <Select value={faultLoc} onValueChange={setFaultLoc}>
              <SelectTrigger data-testid="punt-fault-location"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Kies locatie…</SelectItem>
                {locations.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-data text-muted-foreground">Een storing hoort bij een locatie. Bij een snel punt zonder locatie kies je die hier.</p>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setFaultFor(null)}>Annuleren</Button>
            <Button data-testid="punt-fault-confirm" onClick={doToFault} disabled={busy || faultLoc === "__none__"}>Storing aanmaken</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Locatie erbij zetten, na het opschrijven */}
      <Dialog open={!!locFor} onOpenChange={(o) => !o && setLocFor(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Locatie toevoegen</DialogTitle></DialogHeader>
          <DialogDescription>Waar hoort dit punt bij? Je kunt dit ook later nog wijzigen.</DialogDescription>
          <Select value={newLoc} onValueChange={setNewLoc}>
            <SelectTrigger data-testid="punt-location-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Zonder locatie</SelectItem>
              {locations.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setLocFor(null)}>Annuleren</Button>
            <Button data-testid="punt-location-confirm" onClick={doSetLocation} disabled={busy}>Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Een punt in z'n geheel lezen: wie het opschreef, wanneer, en wat ermee gebeurd is. */}
      <Dialog open={!!detailFor} onOpenChange={(o) => !o && setDetailFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Punt</DialogTitle></DialogHeader>
          <DialogDescription>Zoals het is opgeschreven.</DialogDescription>
          {detailFor && (
            <div className="space-y-3" data-testid="punt-detail">
              {editTitle !== null ? (
                <div className="space-y-2">
                  <Textarea
                    autoFocus
                    rows={3}
                    data-testid="punt-detail-title-input"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveTitle(); } }}
                  />
                  <div className="flex gap-2">
                    <Button data-testid="punt-detail-title-save" onClick={saveTitle} disabled={savingTitle}>Opslaan</Button>
                    <Button variant="secondary" data-testid="punt-detail-title-cancel" onClick={() => setEditTitle(null)}>Annuleren</Button>
                  </div>
                </div>
              ) : writable ? (
                <button
                  type="button"
                  data-testid="punt-detail-title"
                  onClick={() => setEditTitle(detailFor.title)}
                  className="w-full whitespace-pre-wrap break-words text-left text-body text-foreground hover:underline"
                >
                  {detailFor.title}
                </button>
              ) : (
                <p className="whitespace-pre-wrap break-words text-body text-foreground" data-testid="punt-detail-title">
                  {detailFor.title}
                </p>
              )}
              <dl className="space-y-1.5 border-t border-border pt-3 text-body">
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Opgeschreven door</dt>
                  <dd className="text-foreground" data-testid="punt-detail-author">{detailFor.created_by_name || "onbekend"}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Wanneer</dt>
                  <dd data-testid="punt-detail-created">
                    <Code size="small">{formatNlDate((detailFor.created_at || "").slice(0, 10))}</Code>
                  </dd>
                </div>
                {detailFor.location_name && (
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">Locatie</dt>
                    <dd className="text-foreground">{detailFor.location_name}</dd>
                  </div>
                )}
                {detailFor.due_date && (
                  <div className="flex justify-between gap-3">
                    <dt className="text-muted-foreground">Ingepland</dt>
                    <dd><Code size="small">{formatNlDate(detailFor.due_date)}{detailFor.due_time ? ` ${detailFor.due_time}` : ""}</Code></dd>
                  </div>
                )}
                <div className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">Status</dt>
                  <dd className="text-foreground" data-testid="punt-detail-status">
                    {detailFor.converted_fault_id ? "Omgezet naar storing" : detailFor.status === "gedaan" ? "Afgehandeld" : "Open"}
                  </dd>
                </div>
              </dl>
              {detailFor.converted_fault_id && (
                <Button
                  variant="secondary"
                  className="w-full"
                  data-testid="punt-detail-open-fault"
                  onClick={() => { setDetailFor(null); navigate(`/storingen/${detailFor.converted_fault_id}`); }}
                >
                  Storing openen
                </Button>
              )}

              {/* On a phone these three live here and nowhere else. */}
              {writable && !detailFor.converted_fault_id && detailFor.status !== "gedaan" && (
                <div className="grid gap-2 border-t border-border pt-3 sm:hidden" data-testid="punt-detail-actions">
                  <Button variant="outline" className="h-12 justify-start" data-testid="punt-detail-set-location" onClick={() => { const p = detailFor; setDetailFor(null); setLocFor(p); setNewLoc(p.location_id || "__none__"); }}>
                    <MapPin size={18} className="mr-2" /> {detailFor.location_id ? "Andere locatie kiezen" : "Locatie erbij zetten"}
                  </Button>
                  <Button variant="outline" className="h-12 justify-start" data-testid="punt-detail-schedule" onClick={() => { const p = detailFor; setDetailFor(null); setScheduleFor(p); setDueDate(""); setDueTime(""); }}>
                    <CalendarPlus size={18} className="mr-2" /> Plan in
                  </Button>
                  <Button variant="outline" className="h-12 justify-start text-letop-tekst" data-testid="punt-detail-to-fault" onClick={() => { const p = detailFor; setDetailFor(null); setFaultFor(p); setPriority("P3"); setFaultLoc(p.location_id || "__none__"); }}>
                    <AlertTriangle size={18} className="mr-2" /> Is een storing
                  </Button>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDetailFor(null)}>Sluiten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Page>
  );
}
